import { Code, ConnectError, type PromiseClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { HokkaidoR8HouseholderCodeStatus } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { WalletService } from "schema/gen/es/chiikipoint/wallet/v2/service_connect";
import Cancel from "~icons/material-symbols/cancel-outline";
import { css } from "../../../../styled-system/css";
import { LoadingIcon } from "../../../components/loadingIcon";
import { ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";
import { isExpectedConnectError } from "../../../libs/query-client";
import {
  HokkaidoR8HouseholderAmountBlock as AmountBlock,
  HokkaidoR8HouseholderOverlay as Overlay,
  type HokkaidoR8HouseholderReceiveDetail as Detail,
  HokkaidoR8HouseholderReceiveSuccessModal,
} from "./hokkaido-r8-householder-receive-success";

type ErrorView = {
  title: string;
  message?: string;
  showRetry: boolean;
};

type Props = {
  code: string;
  client: PromiseClient<typeof WalletService>;
  onClose: () => void;
};

export function HokkaidoR8HouseholderCodeModal({
  code,
  client,
  onClose,
}: Props) {
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: ["hokkaido-r8-householder-code-status", code],
    queryFn: () => client.getHokkaidoR8HouseholderCodeStatus({ code }),
    retry: false,
    staleTime: 0,
    meta: {
      errorCode:
        ERROR_CODES.CONNECT_GET_HOKKAIDO_R8_HOUSEHOLDER_CODE_STATUS_FAILED,
      expectedConnectCodes: [Code.PermissionDenied],
    },
  });

  const useAsWalletBalanceMeta = {
    errorCode:
      ERROR_CODES.CONNECT_CREATE_HOKKAIDO_R8_HOUSEHOLDER_CODE_USE_AS_WALLET_BALANCE_FAILED,
    expectedConnectCodes: [Code.PermissionDenied, Code.FailedPrecondition],
  } as const;

  const useAsWalletBalance = useMutation({
    mutationFn: () =>
      client.createHokkaidoR8HouseholderCodeUseAsWalletBalance({ code }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["transactions"] });
    },
    throwOnError: (error) =>
      !isExpectedConnectError(error, useAsWalletBalanceMeta),
    meta: useAsWalletBalanceMeta,
  });

  const successDetail = useMemo<Detail | null>(() => {
    const data = useAsWalletBalance.data;
    if (!useAsWalletBalance.isSuccess || !data?.fundSource) return null;
    return { amount: data.amount, fundSource: data.fundSource };
  }, [useAsWalletBalance.isSuccess, useAsWalletBalance.data]);

  const unusedDetail = useMemo<Detail | null>(() => {
    const data = statusQuery.data;
    if (!data?.fundSource) return null;
    if (data.status !== HokkaidoR8HouseholderCodeStatus.UNUSED) return null;
    return { amount: data.amount, fundSource: data.fundSource };
  }, [statusQuery.data]);

  const errorView = useMemo<ErrorView | null>(() => {
    if (useAsWalletBalance.error instanceof ConnectError) {
      const e = useAsWalletBalance.error;
      if (e.code === Code.PermissionDenied) {
        return {
          title: t("シリアルナンバーが利用できません"),
          message: t(
            "世帯主ご本人のマイナンバーカードでログインしていることをご確認ください",
          ),
          showRetry: false,
        };
      }
      if (e.code === Code.FailedPrecondition) {
        return {
          title: t("シリアルナンバーがすでに利用されています"),
          showRetry: false,
        };
      }
    }
    if (
      statusQuery.error instanceof ConnectError &&
      statusQuery.error.code === Code.PermissionDenied
    ) {
      return {
        title: t("シリアルナンバーが利用できません"),
        message: t(
          "世帯主ご本人のマイナンバーカードでログインしていることをご確認ください",
        ),
        showRetry: false,
      };
    }
    const data = statusQuery.data;
    if (!data) return null;
    switch (data.status) {
      case HokkaidoR8HouseholderCodeStatus.UNUSED:
        return null;
      // マイナポータル申請・道庁照合で付与された場合も、このハガキ分はウォレットで受け取り済み
      case HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE:
      case HokkaidoR8HouseholderCodeStatus.USED_AS_MYNA_PORTAL:
      case HokkaidoR8HouseholderCodeStatus.USED_AS_STAFF_MATCH:
        return {
          title: t("受け取り済みです"),
          message: t(
            "このシリアルナンバーのポイントは、すでに受け取られています。",
          ),
          showRetry: false,
        };
      case HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER:
        return {
          title: t("受け取り対象外です"),
          message: t(
            "このシリアルナンバーはギフト券で交付済みのため、ウォレットで受け取ることはできません。",
          ),
          showRetry: false,
        };
      default:
        return {
          title: t("ご利用いただけません"),
          message: t(
            "このシリアルナンバーのステータスを確認できませんでした。",
          ),
          showRetry: true,
        };
    }
  }, [useAsWalletBalance.error, statusQuery.error, statusQuery.data]);

  const handleUseAsWalletBalance = useCallback(() => {
    useAsWalletBalance.mutate();
  }, [useAsWalletBalance]);

  const handleRetry = useCallback(() => {
    void statusQuery.refetch();
  }, [statusQuery]);

  const isSubmitting = useAsWalletBalance.isPending;
  const showLoading = statusQuery.isPending && !errorView;

  if (successDetail) {
    return (
      <HokkaidoR8HouseholderReceiveSuccessModal
        detail={successDetail}
        onClose={onClose}
      />
    );
  }

  return (
    <Overlay>
      {showLoading && (
        <ModalCard>
          <div
            className={css({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "120px",
            })}
          >
            <LoadingIcon />
          </div>
        </ModalCard>
      )}

      {!showLoading && !errorView && unusedDetail && (
        <ModalCard>
          <CardTitle>{t("ポイントを受け取りますか？")}</CardTitle>
          <AmountBlock detail={unusedDetail} />
          <CardBody>
            {t(
              "一度受け取ると、ＪＣＢギフトカードへの変更はできませんので、ご注意ください。",
            )}
          </CardBody>
          <PrimaryButton
            onClick={handleUseAsWalletBalance}
            disabled={isSubmitting}
          >
            {isSubmitting ? <LoadingIcon /> : t("受け取る")}
          </PrimaryButton>
          <TextButton onClick={onClose} disabled={isSubmitting}>
            {t("キャンセル")}
          </TextButton>
        </ModalCard>
      )}

      {!showLoading && errorView && (
        <ModalCard>
          <ErrorIcon />
          <CardTitle>{errorView.title}</CardTitle>
          {errorView.message && <CardBody>{errorView.message}</CardBody>}
          {errorView.showRetry && (
            <PrimaryButton onClick={handleRetry}>{t("再試行")}</PrimaryButton>
          )}
          <OutlinedButton onClick={onClose}>{t("閉じる")}</OutlinedButton>
        </ModalCard>
      )}
    </Overlay>
  );
}

function ModalCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={css({
        bg: "white",
        rounded: "16px",
        p: "24px",
        w: "full",
        maxWidth: "360px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
      })}
    >
      {children}
    </div>
  );
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className={css({
        fontSize: "16px",
        fontWeight: "bold",
        color: "text.primary",
        textAlign: "center",
      })}
    >
      {children}
    </h2>
  );
}

function CardBody({ children }: { children: React.ReactNode }) {
  return (
    <p
      className={css({
        fontSize: "14px",
        color: "text.secondary",
        lineHeight: 1.6,
        textAlign: "center",
      })}
    >
      {children}
    </p>
  );
}

function ErrorIcon() {
  return (
    <div
      className={css({
        fontSize: "56px",
        color: "text.accentError",
      })}
    >
      <Cancel />
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={css({
        bg: disabled ? "surface.secondary" : "text.accentPrimary",
        color: "white",
        padding: "12px",
        borderRadius: "8px",
        width: "100%",
        fontWeight: "bold",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      })}
    >
      {children}
    </button>
  );
}

function TextButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={css({
        padding: "12px",
        borderRadius: "8px",
        width: "100%",
        fontWeight: "bold",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        color: "text.primary",
      })}
    >
      {children}
    </button>
  );
}

function OutlinedButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={css({
        padding: "12px",
        borderRadius: "8px",
        width: "100%",
        fontWeight: "bold",
        cursor: "pointer",
        backgroundColor: "surface.secondary",
        color: "text.primary",
        border: "1px solid",
        borderColor: "border.secondary",
      })}
    >
      {children}
    </button>
  );
}
